<script setup lang="ts">
import { computed } from 'vue';
import type { StructuredAnswer } from '@/ai/utils/types';
import { useInternational } from '@/common/locale';

const { t } = useInternational();

const props = defineProps<{ answer: StructuredAnswer }>();

/** 심각도 → 카드 색 변형 (normal/warning/critical, 그 외 기본). */
const severityClass = computed(() => {
  const s = (props.answer.severity ?? '').toLowerCase();
  if (s === 'critical' || s === 'warning' || s === 'normal') return `structured-answer--${s}`;
  return '';
});
/** 배지에 표기할 심각도 라벨 (대문자). */
const sevLabel = computed(() => (props.answer.severity ?? '').toUpperCase());

const summary = computed(() => props.answer.summary ?? []);
const causes = computed(() => props.answer.causes ?? []);
const actions = computed(() => props.answer.actions ?? []);
</script>

<template>
  <div
    class="structured-answer"
    :class="severityClass"
    data-testid="bot-structured-answer"
  >
    <!-- 헤드라인: 심각도 배지 + verdict -->
    <div class="structured-answer__header">
      <span
        v-if="answer.severity"
        class="structured-answer__sev"
      >
        <span class="structured-answer__sev-dot" />
        {{ sevLabel }}
      </span>
      <!-- div: 챗 본문 p 정규화(:is(p,…){font-size:1em})에 안 걸리게 헤드라인 크기 유지 -->
      <div class="structured-answer__verdict">{{ answer.verdict }}</div>
    </div>

    <!-- 핵심 지표 (label · value) -->
    <dl
      v-if="summary.length > 0"
      class="structured-answer__summary"
    >
      <div
        v-for="(row, idx) in summary"
        :key="`sa-sum-${idx}`"
        class="structured-answer__kv"
      >
        <dt>{{ row.label }}</dt>
        <dd>{{ row.value }}</dd>
      </div>
    </dl>

    <!-- 원인 + 근거 -->
    <section
      v-if="causes.length > 0"
      class="structured-answer__section"
    >
      <div class="structured-answer__section-title">{{ t('WORD.CHATBOT.STRUCTURED.CAUSE') }}</div>
      <ol class="structured-answer__causes">
        <li
          v-for="(c, idx) in causes"
          :key="`sa-cause-${idx}`"
        >
          <span class="structured-answer__num">{{ idx + 1 }}</span>
          <div class="structured-answer__cause-body">
            <div class="structured-answer__cause">{{ c.cause }}</div>
            <div
              v-if="c.evidence"
              class="structured-answer__evidence"
            >
              <span class="structured-answer__evidence-tag">{{
                t('WORD.CHATBOT.MESSAGE.EVIDENCE')
              }}</span
              >{{ c.evidence }}
            </div>
          </div>
        </li>
      </ol>
    </section>

    <!-- 권장조치 -->
    <section
      v-if="actions.length > 0"
      class="structured-answer__section"
    >
      <div class="structured-answer__section-title">
        {{ t('WORD.CHATBOT.STRUCTURED.RECOMMENDATION') }}
      </div>
      <ul class="structured-answer__actions">
        <li
          v-for="(a, idx) in actions"
          :key="`sa-act-${idx}`"
        >
          <span
            class="structured-answer__check"
            aria-hidden="true"
            >✓</span
          >
          <span>{{ a }}</span>
        </li>
      </ul>
    </section>
  </div>
</template>

<style scoped lang="scss">
.structured-answer {
  --sa-accent: var(--color-gray-04-06, #9aa0a8);
  --sa-tint: var(--color-gray-01-02, #f7f8fa);
  --sa-on-tint: var(--sub-font-color, #555b63);

  margin-top: 8px;
  overflow: hidden;
  border: 1px solid var(--color-gray-02-06, #e0e3e8);
  border-left: 3px solid var(--sa-accent);
  border-radius: 8px;
  background-color: var(--config-group-box-bg-color, #ffffff);
  font-size: 13px; /* 챗봇 본문(general-message__content 13px)과 일치 */

  &--normal {
    /* --token-success-text-color 는 이 테마에서 회색(#edf0f2)으로 매핑돼 글자가 거의 안 보임 →
       실제 녹색 팔레트(green-06)로 고정. critical 이 --color-red-05 을 양테마 공용으로 쓰는 것과 동일 패턴. */
    --sa-accent: var(--color-green-06, #059669);
    --sa-tint: var(--token-success-bg-color, #e6f7ed);
    --sa-on-tint: var(--color-green-06, #059669);
  }
  &--warning {
    /* --tag-orange-font-color 는 태그 전용 토큰이라 light 에서 #9a3412(적갈색)로 매핑돼
       critical(#ef4444)과 같은 붉은 계열로 읽힌다 → 제품 표준 WARNING 앰버
       (--chart-warning-color 등이 쓰는 yellow-06)로 양테마 공용 고정.
       normal 이 green-06, critical 이 red-05 를 공용으로 쓰는 것과 동일 패턴. */
    --sa-accent: var(--color-yellow-06, #d97706);
    --sa-tint: var(--feedback-warning-bg-color, #fff5e6);
    --sa-on-tint: var(--color-yellow-06, #d97706);
  }
  &--critical {
    --sa-accent: var(--connection-test-fail-color, #d32f2f);
    --sa-tint: var(--feedback-error-bg-color, #fdecea);
    --sa-on-tint: var(--connection-test-fail-color, #d32f2f);
  }

  /* ── 헤더 ── */
  &__header {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--color-gray-01-04, #eceef1);
    background-color: var(--sa-tint);
  }

  &__sev {
    display: inline-flex;
    gap: 5px;
    align-items: center;
    align-self: flex-start;
    padding: 3px 9px;
    border-radius: 10px;
    background-color: var(--config-group-box-bg-color, #ffffff);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.4px;
    color: var(--sa-on-tint);
  }

  &__sev-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--sa-accent);
  }

  &__verdict {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.5;
    color: var(--main-font-color, #1f2329);
  }

  /* ── 핵심 지표 ── */
  &__summary {
    display: flex;
    flex-direction: column;
    padding: 8px 12px;
    margin: 0;
  }

  &__kv {
    display: flex;
    gap: 10px;
    align-items: baseline;
    padding: 5px 0;

    & + & {
      border-top: 1px solid var(--color-gray-01-04, #f0f1f3);
    }

    dt {
      flex: 0 0 34%;
      font-weight: 600;
      color: var(--sub-font-color, #4e555f);
    }

    dd {
      flex: 1;
      margin: 0;
      font-weight: 700;
      color: var(--main-font-color, #1f2329);
    }
  }

  /* ── 섹션 (원인/권장조치) ── */
  &__section {
    padding: 8px 12px;
    border-top: 1px solid var(--color-gray-01-04, #eceef1);
  }

  &__section-title {
    margin-bottom: 7px;
    font-size: 12px;
    font-weight: 800;
    color: var(--sa-accent);
  }

  /* ── 원인 ── */
  &__causes {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      display: flex;
      gap: 8px;
      align-items: flex-start;
    }
  }

  &__num {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: var(--sa-accent);
    font-size: 11px;
    font-weight: 800;
    line-height: 18px;
    color: #ffffff;
    text-align: center;
  }

  &__cause {
    font-weight: 600;
    line-height: 1.55;
    color: var(--main-font-color, #1f2329);
  }

  &__evidence {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.55;
    color: var(--sub-font-color, #545b65);
  }

  &__evidence-tag {
    display: inline-block;
    padding: 1px 6px;
    margin-right: 5px;
    border-radius: 3px;
    background-color: var(--sa-tint);
    font-size: 10px;
    font-weight: 800;
    color: var(--sa-on-tint);
  }

  /* ── 권장조치 ── */
  &__actions {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      display: flex;
      gap: 7px;
      align-items: flex-start;
      font-weight: 500;
      line-height: 1.55;
      color: var(--main-font-color, #1f2329);
    }
  }

  &__check {
    flex-shrink: 0;
    margin-top: 1px;
    font-size: 12px;
    font-weight: 800;
    color: var(--sa-accent);
  }
}
</style>
